'use client';
import { useCallback, useContext, useState } from 'react';
import { SafeType } from '@benzinga/safe-await';
import { Authentication, AuthenticationManager, RegisterUser } from '@benzinga/session';
import { SessionContext } from '@benzinga/session-context';

import { AuthMethod } from '../types';

const SOCIAL_OAUTH_PATH: Record<Exclude<AuthMethod, 'email' | 'google-one-tap' | 'social-fallback'>, string> = {
  apple: 'apple-id',
  google: 'google-oauth2',
  microsoft: 'microsoft-graph',
};

export interface UseAuthState {
  loading: boolean;
  error: string | null;
}

export interface UseAuthReturn extends UseAuthState {
  login: (email: string, password: string, captcha?: string) => Promise<SafeType<Authentication>>;
  register: (user: RegisterUser, registerType?: string) => Promise<SafeType<Authentication>>;
  startSocialAuth: (provider: 'apple' | 'google' | 'microsoft', returnUrl: string, iframe?: boolean) => void;
  handleSocialReturn: () => Promise<SafeType<Authentication>>;
  clearError: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const session = useContext(SessionContext);
  const [state, setState] = useState<UseAuthState>({ error: null, loading: false });

  const setLoading = useCallback((loading: boolean) => setState(prev => ({ ...prev, loading })), []);
  const setError = useCallback((error: string | null) => setState(prev => ({ ...prev, error })), []);

  const login = useCallback<UseAuthReturn['login']>(
    async (email, password, captcha) => {
      setLoading(true);
      setError(null);
      try {
        const auth = await session.getManager(AuthenticationManager).login(email, password, captcha);
        if (auth.err) setError(auth.err.message ?? 'Login failed');
        return auth;
      } finally {
        setLoading(false);
      }
    },
    [session, setLoading, setError],
  );

  const register = useCallback<UseAuthReturn['register']>(
    async (user, registerType) => {
      setLoading(true);
      setError(null);
      try {
        const auth = await session.getManager(AuthenticationManager).register(user, registerType);
        if (auth.err) setError(auth.err.message ?? 'Registration failed');
        return auth;
      } finally {
        setLoading(false);
      }
    },
    [session, setLoading, setError],
  );

  const startSocialAuth = useCallback<UseAuthReturn['startSocialAuth']>((provider, returnUrl, iframe = false) => {
    const path = SOCIAL_OAUTH_PATH[provider];
    const encoded = encodeURIComponent(returnUrl);
    const url = `https://accounts.benzinga.com/oauth/login/${path}/?embedded=${iframe ? 'true' : 'false'}&next=${encoded}`;
    // _top forces navigation out of any iframe context.
    if (typeof window !== 'undefined') window.open(url, '_top');
  }, []);

  const handleSocialReturn = useCallback<UseAuthReturn['handleSocialReturn']>(async () => {
    setLoading(true);
    setError(null);
    try {
      // Force refresh — the anonymous session pre-OAuth needs to be replaced by the cookie set by the callback.
      const auth = await session.getManager(AuthenticationManager).getAuthSession(true);
      if (auth.err) setError(auth.err.message ?? 'Social authentication failed');
      return auth;
    } finally {
      setLoading(false);
    }
  }, [session, setLoading, setError]);

  const clearError = useCallback(() => setError(null), [setError]);

  return {
    ...state,
    clearError,
    handleSocialReturn,
    login,
    register,
    startSocialAuth,
  };
};
